// E-Fatura Entegrasyon Ayarları penceresi kullanım rehberi. Metinler 'guides' çeviri alanında.
import type { TFunction } from 'i18next';
import type { GuideContent } from '../../components/common/UsageGuideDialog';

const G = 'documents.eInvoiceIntegration';

export const eInvoiceIntegrationGuide = (t: TFunction): GuideContent => ({
  layout: 'tabbed',
  title: t(`${G}.title`, 'E-fatura entegrasyonu nasıl kurulur?'),
  intro: t(`${G}.intro`, 'Bu pencerede e-fatura sağlayıcınızın (entegratör) kullanıcı bilgilerini girer, bağlantıyı test eder ve entegrasyonu aktif edersiniz. Haritada bir bölgeye tıklayarak o adıma geçebilirsiniz.'),
  regionLabels: {
    header: t(`${G}.regions.header`, 'Başlık'),
    tabs: t(`${G}.regions.tabs`, 'Sağlayıcı sekmeleri'),
    body: t(`${G}.regions.body`, 'Entegrasyon formu'),
    footer: t(`${G}.regions.footer`, 'Kaydet / Kapat'),
  },
  navItems: [
    t(`${G}.nav.finansbank`, 'Finansbank'),
    t(`${G}.nav.edm`, 'EDM'),
    t(`${G}.nav.pavopay`, 'PavoPay'),
  ],
  steps: [
    {
      region: 'header',
      title: t(`${G}.s1.title`, '1. Bu pencere ne işe yarar?'),
      text: t(`${G}.s1.text`, 'E-faturalarınız seçtiğiniz entegratör üzerinden GİB\'e gönderilir ve gelen faturalar oradan okunur. Entegratörden aldığınız kullanıcı adı ve şifreyi burada bir kez tanımlarsınız.'),
      tips: [
        t(`${G}.s1.tip1`, 'Bilgileriniz şifrelenmiş olarak saklanır, yalnız e-fatura işlemleri için kullanılır ve üçüncü taraflarla paylaşılmaz.'),
      ],
    },
    {
      region: 'tabs',
      title: t(`${G}.s2.title`, '2. Sağlayıcıyı seçin — aynı anda yalnız biri aktif'),
      text: t(`${G}.s2.text`, 'Finansbank, EDM ve PavoPay sekmeleri ayrı ayrı ayarlanır. Sekme adının yanındaki "Aktif" / "Pasif" etiketi durumu gösterir. Finansbank ve EDM\'den aynı anda yalnız biri aktif olabilir.'),
      tips: [
        t(`${G}.s2.tip1`, 'Diğeri aktifken bu sekmedeki bilgiler değiştirilemez, kaydedilemez, silinemez ve aktif edilemez. Önce aktif olanı pasif yapın.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s3.title`, '3. Firma ve kullanıcı bilgilerini girin'),
      text: t(`${G}.s3.text`, 'Ticari Ünvan ve Vergi Numarası firma bilgilerinizden kendiliğinden gelir. Kullanıcı Adı ve Şifre entegratörünüzün size verdiği bilgilerdir. Bu dört alan zorunludur.'),
      tips: [
        t(`${G}.s3.tip1`, 'Kayıtlı bir entegrasyonda şifre gizli görünür; değiştirmezseniz mevcut şifre korunur.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s4.title`, '4. EDM: ortam, seri, GB etiketi ve banka'),
      text: t(`${G}.s4.text`, 'Ortam anahtarı Test veya Canlı seçer. Test\'te faturalar EDM test ortamına gider, GİB\'e gerçek belge gitmez; Canlı\'da faturalar GERÇEK olarak GİB\'e gönderilir. Kayıttan sonra "EDM\'den Serileri Getir" ve "EDM\'den Etiketleri Getir" ile hesabınızdaki seri ve GB etiketlerini listeden seçebilirsiniz.'),
      tips: [
        t(`${G}.s4.tip1`, 'Canlıya geçmeden: kullanıcı adı / şifre canlı hesaba ait olmalı, seriler canlı hesaptan seçilmeli, sonra Kaydet ve "Bağlantıyı Test Et".'),
        t(`${G}.s4.tip2`, 'E-Fatura ve E-Arşiv seri öneki boş bırakılırsa gönderimde kendiliğinden seçilir.'),
        t(`${G}.s4.tip3`, 'GB Etiketi, EDM hesabınızın gönderici birim etiketiyle birebir aynı olmalıdır; boşsa gönderimde kendiliğinden çözülür.'),
        t(`${G}.s4.tip4`, 'Faturada Gösterilecek Banka Hesapları: seçtiğiniz hesapların IBAN\'ı faturaya yazılır; hiçbiri seçilmezse banka kutusu görünmez.'),
        t(`${G}.s4.tip5`, 'Listeden seçim yaptıktan sonra Kaydet\'e basmayı unutmayın.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s5.title`, '5. Kaydedin, test edin, aktif edin'),
      text: t(`${G}.s5.text`, 'Önce Kaydet\'e basın; aktif / pasif anahtarı yalnız kayıtlı entegrasyonda açılır. Anahtarı açınca e-fatura işlemleri bu entegrasyonla yapılır.'),
      tips: [
        t(`${G}.s5.tip1`, 'EDM\'de "Bağlantıyı Test Et" kullanıcı bilgilerinizi doğrular; "VKN / TCKN sorgula" bir firmanın e-fatura kullanıcısı olup olmadığını ve posta kutularını gösterir.'),
        t(`${G}.s5.tip2`, '"Bu Entegrasyonu Sil" kayıtlı entegrasyonu onay sorduktan sonra siler.'),
      ],
    },
    {
      region: 'body',
      title: t(`${G}.s6.title`, '6. PavoPay: her şube için ayrı entegrasyon'),
      text: t(`${G}.s6.text`, 'PavoPay (Aktif Bank e-Dönüşüm) entegrasyonu şube bazlı çalışır; her şube için ayrı bir entegrasyon ekleyebilirsiniz. Şube (isteğe bağlı), Kullanıcı Adı ve Şifre girip bu sekmedeki Kaydet\'e basın.'),
      tips: [
        t(`${G}.s6.tip1`, 'Şube seçmezseniz şubesiz (genel) entegrasyon olur; e-fatura sayfasını yönetici olarak kullanmak için bu gereklidir.'),
        t(`${G}.s6.tip2`, '"Kayıtlı Entegrasyonlar" listesinde her satırı test edebilir veya silebilirsiniz; "Tüm Entegrasyonları Yönet" PavoPay Entegrasyonları sayfasını açar.'),
      ],
    },
    {
      region: 'footer',
      title: t(`${G}.s7.title`, '7. Kaydet ve kapat'),
      text: t(`${G}.s7.text`, 'Alttaki Kaydet Finansbank ve EDM sekmeleri içindir (PavoPay\'in kendi Kaydet düğmesi vardır). Kapat ile pencereden çıkarsınız. Başlıktaki (i) bu rehberi yeniden açar.'),
    },
  ],
});
